import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import CircularProgress from '@mui/material/CircularProgress';
import { Step } from '../types';
import ErrorLoadingData from '../components/ErrorLoadingData';
import { ApiContext } from './ApiContext';
import { API_V1_HOSTNAME_KEY, BACK_OFFICE_LINK_KEY } from '../localStorageKeys';

/* eslint-disable @typescript-eslint/no-empty-function */
const StepContext = React.createContext({
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  updateStepCompleted: (_status: string) => {},
  currentStepIndex: -1,
  steps: [] as Step[],
});
/* eslint-enable @typescript-eslint/no-empty-function */

const StepContextProvider = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { apiFetch } = useContext(ApiContext);
  const [steps, setSteps] = useState<Step[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);

  // Fetch onboarding steps for the account ---------------------------------------------------------------------------
  const {
    refetch: refetchStepData,
    data: stepsData,
    isLoading,
    isError,
  } = useQuery<Step[]>(
    'steps',
    () =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/steps?module=wizard`,
        false
      ),
    {
      enabled: steps.length === 0,
      onError(err: any) {
        if ((err as any)?.code === 401) return;
        if ((err as any)?.code === 403) {
          window.location.href = localStorage.getItem(BACK_OFFICE_LINK_KEY);
          return;
        }
        Sentry.captureException(new Error('Error fetching steps'), {
          extra: { error: JSON.stringify(err) },
        });
      },
    }
  );

  const { isLoading: isUpdatingStepCompleted, mutate: updateStepCompleted } = useMutation(
    (status: string) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/steps/${
          steps[currentStepIndex].stepKey
        }`,
        { status },
        'POST'
      ),
    {
      async onSuccess() {
        await queryClient.invalidateQueries('steps');
        refetchStepData();
      },
      onError(err: any) {
        if ((err as any)?.code === 401) return;
        Sentry.captureException(new Error('Error updating step completed'), {
          extra: { error: JSON.stringify(err) },
        });
      },
    }
  );

  // Navigation between steps methods ---------------------------------------------------------------------------------
  const navigateAndReset = (path: string, replace = false) => {
    window.scrollTo(0, 0); // scroll to top to show next step
    navigate(path, { replace });
  };

  // Go to the first completed page
  // OR go to the first page (without marking any steps as completed)
  // If all steps are completed, send to confirmation
  const checkCompletedStepsAndNavigate = (stepsDataForNav: Step[], isFirstLoad = false) => {
    if (!Array.isArray(stepsDataForNav) || stepsDataForNav?.length === 0) {
      window.location.href = localStorage.getItem(BACK_OFFICE_LINK_KEY);
      return;
    }

    const firstStepNotCompleted = stepsDataForNav.findIndex((step) => !step.completedAt);
    if (firstStepNotCompleted === -1) {
      navigateAndReset(`/confirmation`);
      return;
    }

    // If no steps are completed yet we want to lead to the welcome page
    // Unless we already are at the welcome page, then we want to proceed to the next as normal
    if (firstStepNotCompleted === 0 && isFirstLoad) {
      navigateAndReset('/');
      return;
    }

    setCurrentStepIndex(firstStepNotCompleted);
    navigateAndReset(`/${stepsDataForNav[firstStepNotCompleted].stepKey}`);
  };

  const goToNextStep = () => {
    if (stepsData?.length === 0) {
      window.location.href = localStorage.getItem(BACK_OFFICE_LINK_KEY);
      return;
    }

    // When clicking "Next" on Welcome page, we want to go to the first completed page
    // OR go to the first page (without marking any steps as completed)
    // If all steps are completed, send to confirmation
    if (window.location.pathname === '/') {
      checkCompletedStepsAndNavigate(steps, false);
      return;
    }

    if (steps[currentStepIndex]?.completedAt === null) {
      updateStepCompleted('completed');
    }

    if (currentStepIndex + 1 < steps.length) {
      const newIndex = currentStepIndex + 1;
      setCurrentStepIndex(newIndex);
      navigateAndReset(`/${steps[newIndex].stepKey}`);
    } else {
      navigateAndReset('/confirmation');
    }
  };

  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      const newIndex = currentStepIndex - 1;
      setCurrentStepIndex(newIndex);
      navigateAndReset(`/${steps[newIndex].stepKey}`);
    }
  };

  useEffect(() => {
    if (stepsData) {
      setSteps(stepsData);
      checkCompletedStepsAndNavigate(stepsData, true);
    }
  }, [stepsData]);

  const contextValue = {
    goToNextStep,
    goToPreviousStep,
    updateStepCompleted,
    currentStepIndex,
    steps,
  };

  if (isLoading || isUpdatingStepCompleted) {
    return (
      <CircularProgress
        aria-label="Loading Data"
        aria-live="polite"
        aria-busy={isLoading || isUpdatingStepCompleted}
        data-testid="loading-icon"
        sx={{ color: 'text.secondary' }}
      />
    );
  }
  if (isError) {
    return <ErrorLoadingData action={refetchStepData} />;
  }

  return (
    <StepContext.Provider value={contextValue}>
      <Outlet />
    </StepContext.Provider>
  );
};

export { StepContext, StepContextProvider };
